import { useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export default function CartPage() {
  const navigate = useNavigate();
  const { cart, updateCartItem, removeFromCart, cartTotal, loading } = useCart();
  const { user } = useAuth();

  if (!user) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="container mx-auto px-4 py-16">
          {cart.items?.length === 0 ? (
            <div className="text-center">
              <ShoppingBag className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <h2 className="text-2xl font-bold mb-2">Il carrello è vuoto</h2>
              <p className="text-gray-600 mb-6">Aggiungi prodotti per iniziare</p>
              <Button
                onClick={() => navigate('/products')}
                className="bg-rose-600 hover:bg-rose-700 text-white rounded-full font-bold px-8 py-6"
              >
                Continua gli Acquisti
              </Button>
            </div>
          ) : (
            <>
              <div className="max-w-4xl mx-auto mb-8 p-6 bg-gradient-to-r from-emerald-50 to-rose-50 border-2 border-emerald-300 rounded-2xl">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                  🛒 Hai {cart.items.length} prodotto{cart.items.length > 1 ? 'i' : ''} nel carrello!
                </h2>
                <p className="text-center text-gray-700 mb-6">
                  Scegli come procedere con il tuo ordine:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <Button
                    onClick={() => navigate('/guest-checkout')}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold py-6 px-4 text-base shadow-lg hover:shadow-xl transition-all h-auto"
                    data-testid="guest-checkout-button"
                  >
                    <div className="flex flex-col items-center gap-1 w-full">
                      <ShoppingBag className="h-6 w-6 mb-1" />
                      <span className="text-base leading-tight">Ordina Senza Registrazione</span>
                      <span className="text-xs font-normal opacity-90 leading-tight">Ritiro e pagamento in negozio</span>
                    </div>
                  </Button>
                  <Button
                    onClick={() => document.querySelector('[data-testid="login-button"]')?.click()}
                    variant="outline"
                    className="border-2 border-rose-600 text-rose-600 hover:bg-rose-50 rounded-xl font-bold py-6 px-4 text-base h-auto"
                  >
                    <div className="flex flex-col items-center gap-1 w-full">
                      <span className="text-base leading-tight">Accedi / Registrati</span>
                      <span className="text-xs font-normal leading-tight">Più opzioni pagamento</span>
                    </div>
                  </Button>
                </div>
              </div>

              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl font-bold mb-6">Il Tuo Carrello</h2>
                <div className="space-y-4">
                  {cart.items.map((item) => (
                    <div
                      key={item.product_id}
                      className={`flex gap-4 p-4 bg-white border rounded-xl ${item.is_preorder ? 'border-blue-300 bg-blue-50' : 'border-gray-200'}`}
                      data-testid={`cart-item-${item.product_id}`}
                    >
                      <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                        {item.image_url && (
                          <img
                            src={item.image_url}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>

                      <div className="flex-1">
                        <h3 className="font-bold text-lg mb-1">{item.name}</h3>
                        {item.is_preorder && (
                          <span className="inline-block bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-1 rounded-full mb-1">
                            📅 Prenotazione - Disponibile dal {new Date(item.availability_date).toLocaleDateString('it-IT')}
                          </span>
                        )}
                        <p className="text-emerald-600 font-bold mb-2">€{item.price.toFixed(2)}</p>

                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleUpdateQuantity(item.product_id, item.quantity - 1)}
                            className="w-8 h-8 flex items-center justify-center border rounded-lg hover:bg-gray-100"
                            data-testid={`decrease-${item.product_id}`}
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="w-12 text-center font-semibold" data-testid={`quantity-${item.product_id}`}>
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => handleUpdateQuantity(item.product_id, item.quantity + 1)}
                            className="w-8 h-8 flex items-center justify-center border rounded-lg hover:bg-gray-100"
                            data-testid={`increase-${item.product_id}`}
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      <button
                        onClick={() => handleRemove(item.product_id)}
                        className="text-red-600 hover:text-red-700 p-2"
                        data-testid={`remove-${item.product_id}`}
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                  <div className="flex justify-between text-xl font-bold">
                    <span>Totale:</span>
                    <span className="text-emerald-600">€{cartTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  const handleUpdateQuantity = async (productId, newQuantity) => {
    if (newQuantity < 1) return;
    try {
      await updateCartItem(productId, newQuantity);
    } catch (error) {
      toast.error('Errore durante l\'aggiornamento');
    }
  };

  const handleRemove = async (productId) => {
    try {
      await removeFromCart(productId);
      toast.success('Prodotto rimosso');
    } catch (error) {
      toast.error('Errore durante la rimozione');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="container mx-auto px-4 py-16 text-center">
          <p>Caricamento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-8">
        <h1 className="text-4xl font-extrabold mb-8" data-testid="cart-title">Il Tuo Carrello</h1>

        {cart.items?.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-cart">
            <ShoppingBag className="h-16 w-16 mx-auto text-gray-400 mb-4" />
            <h2 className="text-2xl font-bold mb-2">Il carrello è vuoto</h2>
            <p className="text-gray-600 mb-6">Aggiungi prodotti per iniziare</p>
            <Button
              onClick={() => navigate('/products')}
              className="bg-rose-600 hover:bg-rose-700 text-white rounded-full font-bold px-8 py-6"
              data-testid="continue-shopping"
            >
              Continua gli Acquisti
            </Button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4">
              {cart.items.map((item) => (
                <div
                  key={item.product_id}
                  className={`flex gap-4 p-4 bg-white border rounded-xl ${item.is_preorder ? 'border-blue-300 bg-blue-50' : 'border-gray-200'}`}
                  data-testid={`cart-item-${item.product_id}`}
                >
                  <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                    {item.image_url && (
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>

                  <div className="flex-1">
                    <h3 className="font-bold text-lg mb-1">{item.name}</h3>
                    {item.is_preorder && (
                      <span className="inline-block bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-1 rounded-full mb-1">
                        📅 Prenotazione - Disponibile dal {new Date(item.availability_date).toLocaleDateString('it-IT')}
                      </span>
                    )}
                    <p className="text-emerald-600 font-bold mb-2">€{item.price.toFixed(2)}</p>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleUpdateQuantity(item.product_id, item.quantity - 1)}
                        className="w-8 h-8 flex items-center justify-center border rounded-lg hover:bg-gray-100"
                        data-testid={`decrease-${item.product_id}`}
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="w-12 text-center font-semibold" data-testid={`quantity-${item.product_id}`}>
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => handleUpdateQuantity(item.product_id, item.quantity + 1)}
                        className="w-8 h-8 flex items-center justify-center border rounded-lg hover:bg-gray-100"
                        data-testid={`increase-${item.product_id}`}
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={() => handleRemove(item.product_id)}
                    className="text-red-600 hover:text-red-700 p-2"
                    data-testid={`remove-${item.product_id}`}
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>

            <div className="lg:col-span-1">
              <div className="bg-orange-50 p-6 rounded-2xl border border-gray-200 sticky top-24">
                <h2 className="text-2xl font-bold mb-4">Riepilogo Ordine</h2>

                <div className="space-y-2 mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotale</span>
                    <span className="font-semibold" data-testid="cart-subtotal">€{cartTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xl font-bold pt-2 border-t">
                    <span>Totale</span>
                    <span className="text-emerald-600" data-testid="cart-total">€{cartTotal.toFixed(2)}</span>
                  </div>
                </div>

                <Button
                  onClick={() => navigate('/checkout')}
                  className="w-full bg-rose-600 hover:bg-rose-700 text-white rounded-full font-bold py-6"
                  data-testid="proceed-to-checkout"
                >
                  Procedi al Checkout
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}